// Global Instructions Rule Applied!
//
// New commitment form (/commitments/forms/full/new) — adds an investor's
// commitment to a fund or to a consolidated fund, and where needed the
// investor entity itself, in one step. Opened from the account's Funds tab
// (?account=), the Investor form (?investor=&account=), a fund's Investors
// tab (?fund=) and a consolidated fund's Investors tab (?fund_group=); what
// the form was opened for is filled in and fixed.
//
// Sections: Investor (the account, then one of its investor entities or a new
// one), Fund (one select over the standalone funds and the consolidated
// funds — a member fund is not offered), and then the sections of the chosen
// kind: Commitment, Terms (a fund) or Allocation (a consolidated fund,
// everything in the primary fund to start), and Capital call notices.
// [Cancel] [Create]; after Create the tab becomes the commitment's own form.

import React from 'react'
import { Alert, Col, Row, Spin } from 'antd'

import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { useNewCommitmentForm } from '../../hooks/useNewCommitmentForm.ts'
import { CommitmentAllocationSection } from '../CommitmentAllocationSection.tsx'
import { CommitmentFormHeader } from '../CommitmentFormHeader.tsx'
import { CommitmentNoticeSection, NOTICE_CONSOLIDATED_HELP } from '../CommitmentNoticeSection.tsx'
import { CommitmentTermsSection } from '../CommitmentTermsSection.tsx'

export default function NewCommitmentForm(): React.ReactElement {
  const form = useNewCommitmentForm()

  if (form.loading) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  const inputProps = {
    inputMode: 'edit' as const,
    labelPosition: 'above' as const,
    onChange: form.handleChange,
    validationErrors: form.validationErrors,
    validationErrorsVersion: form.validationErrorsVersion
  }

  return (
    <div className='h-full overflow-y-auto p-4'>
      <CommitmentFormHeader
        title='New Commitment'
        creating
        dirty={form.dirty}
        saving={form.saving}
        onSave={() => void form.handleSave()}
        onCancel={form.handleCancel}
        onClose={form.handleCancel}
      />

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={12}>
          <FormSection title='Investor' titleColor='blue' scrollable={false}>
            <InputManager {...inputProps} fields={form.accountFields} />
            {/* another account has other investor entities: the select starts over */}
            <InputManager key={`investorFields-${form.accountKey}`} {...inputProps} fields={form.investorFields} />
            {form.newInvestorFields.length > 0 ? <InputManager {...inputProps} fields={form.newInvestorFields} /> : null}
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Fund' titleColor='blue' scrollable={false}>
            <InputManager {...inputProps} fields={form.targetFields} />
            {form.targetUnavailable ? (
              <Alert
                className='mt-2'
                type='warning'
                showIcon
                message='This fund is part of a consolidated fund. Its commitments are added to the consolidated fund: choose it above.'
              />
            ) : null}
          </FormSection>
        </Col>

        {form.kind ? (
          <>
            <Col xs={24} xl={form.kind === 'consolidated' ? 10 : 12}>
              <FormSection title='Commitment' titleColor='blue' scrollable={false}>
                <InputManager {...inputProps} fields={form.commitmentFields} />
              </FormSection>
            </Col>
            {form.kind === 'fund' ? (
              <Col xs={24} xl={12}>
                <CommitmentTermsSection
                  terms={form.terms}
                  fundFeeRate={form.fundFeeRate}
                  fundDefaults={form.fundDefaults}
                  onChange={form.handleChange}
                />
              </Col>
            ) : (
              <Col xs={24} xl={14}>
                <CommitmentAllocationSection
                  rows={form.allocations}
                  core={form.core}
                  statusOptions={form.statusOptions}
                  currencySymbol={form.currencySymbol}
                  onRowChange={form.handleAllocationChange}
                  onApplyDefaults={form.handleApplyDefaults}
                />
              </Col>
            )}
            <Col xs={24}>
              <CommitmentNoticeSection
                notice={form.notice}
                investorTemplateIds={form.investorTemplateIds}
                options={form.noticeSupplementOptions}
                onChange={form.handleNoticeChange}
                help={form.kind === 'consolidated' ? NOTICE_CONSOLIDATED_HELP : undefined}
              />
            </Col>
          </>
        ) : null}
      </Row>
    </div>
  )
}
